import React, { useRef } from 'react';

import cx from 'classnames';
import { CompositeDecorator, Editor as DraftJSEditor, EditorState, Modifier } from 'draft-js';

import Text from 'components/shared/Typography/Text';
import { useFormFieldPopup } from 'hooks/useFormFieldPopup';
import * as draftUtils from 'utils/draft';
import { isDefined, isNotDefined } from 'utils/ramda';

import { EDITOR_TYPES, PAGE_MENTION_ENTITY_TYPE } from './constants';
import { EditorContextProvider, useEditorContext, useMentionDecorator, usePageMentionEntity } from './context';
import CreateNewPage from './CreateNewPage';
import EditorErrorBoundary from './EditorErrorBoundary';
import styles from './index.module.css';
import MentionSuggestions from './MentionSuggestions';
import getSuggestionsStrategy from './MentionSuggestionsStrategy';
import MyOwnAreaSelect from './MyOwnAreaSelect';
import PageEntityEditor from './PageEntityEditor';

function DetailsTooltip() {
  return (
    <Text accent style={{ fontSize: '1.5rem' }}>
      <b>Tool Tip:</b>
      <br />
      <br />
      Include as many or as few details as you like. You may want to include information about transfers and flights or
      daily activities.
      <br />
      <br />
      In a rush? You can even leave the details fields blank.
      <br />
      <br />
      Use the @ symbol to include pages from your Content Library.
      <br />
      <br />
      Any page you pull in here will appear in the itinerary in the same order it is entered into this table.
      <br />
      <br />
      <b>Examples:</b> <br />
      <br />
      “Fly via @PrivateCharterAircraft to Namunyak, departing at 10am and arriving at 11am”
      <br />
      <br />
      “Early morning @HotAirBalloon...”
      <br />
      <br />
      “A <span style={{ wordBreak: 'break-all' }}>@FullDayWinelandsPrivateTour</span> will include...”
    </Text>
  );
}

function AccommodationTooltip() {
  return (
    <Text accent style={{ fontSize: '1.5rem' }}>
      <b>Tool Tip:</b>
      <br />
      <br />
      Include the accommodation name with an @ symbol.
      <br />
      <br />
      If you want to include multiple accommodation options, use the word{' '}
      <span className="textBold">&quot;or&quot;</span> and an @ symbol for other options.
      <br />
      <br />
      <b>Examples:</b> <br />
      <br />
      “Fly via @PrivateCharterAircraft to Namunyak, departing at 10am and arriving at 11am”
      <br />
      <br />
      “Overnight @Emakoko”
      <br />
      <br />
      “Overnight @LewaHouse or @KifaruHouse”
    </Text>
  );
}

const editorType2TooltipMap = {
  [EDITOR_TYPES.DETAILS]: DetailsTooltip,
  [EDITOR_TYPES.ACCOMMODATION]: AccommodationTooltip,
};

function Editor({ type, anchorRef, placeholder, readOnly, spellCheck }) {
  const { editorState, setEditorState, editorProps } = useEditorContext();
  const showPopup = useFormFieldPopup();
  const Tooltip = editorType2TooltipMap[type];

  return (
    <DraftJSEditor
      // in order to fix this https://github.com/facebook/draft-js/issues/2422
      handleBeforeInput={(chars, editorState) => {
        if (chars === '. ') {
          const currentSelection = editorState.getSelection();
          setEditorState(
            EditorState.set(editorState, {
              currentContent: Modifier.replaceText(editorState.getCurrentContent(), currentSelection, ' '),
            }),
          );
          return 'handled';
        }
        return 'not-handled';
      }}
      readOnly={readOnly}
      placeholder={placeholder}
      editorState={editorState}
      onChange={draftUtils.secureOnChange(setEditorState)}
      onFocus={showPopup(<Tooltip />)}
      ref={anchorRef}
      stripPastedStyles
      spellCheck={spellCheck}
      {...editorProps}
    />
  );
}

const MentionDecorator = (props) => {
  const containerRef = useRef();
  useMentionDecorator(props, containerRef?.current);

  return (
    <span ref={containerRef} className={styles.spanHandler}>
      {props.children}
    </span>
  );
};

function PageMentionEntity(props) {
  const { children, entityKey, offsetKey } = props;
  const entityRef = useRef();
  const { onClick } = usePageMentionEntity(props, entityRef?.current);

  const { editorState, pages } = useEditorContext();
  const meta = editorState.getCurrentContent().getEntity(entityKey).getData();

  if (meta.type === 'property' && isDefined(pages)) {
    return (
      <span
        ref={entityRef}
        onMouseDown={onClick}
        data-offset-key={offsetKey}
        id={entityKey}
        className={cx(styles.mentionEntity, styles.mentionEntityEditable)}
      >
        {children}
      </span>
    );
  }

  return (
    <span data-offset-key={offsetKey} id={entityKey} className={styles.mentionEntity}>
      {children}
    </span>
  );
}

function findPageMentionEntities(contentBlock, callback, contentState) {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();

    if (isNotDefined(entityKey)) return false;

    return contentState.getEntity(entityKey).getType() === PAGE_MENTION_ENTITY_TYPE;
  }, callback);
}

const decorators = new CompositeDecorator([
  {
    strategy: findPageMentionEntities,
    component: PageMentionEntity,
  },
  {
    strategy: getSuggestionsStrategy(),
    component: MentionDecorator,
  },
]);

export default function MentionEditor({
  initialRawContentState,
  pages = [],
  onContentStateChange,
  placeholder,
  type,
  readOnly,
  mountEl,
  onRemoveMention,
  additionalFilterQueryParams = {},
}) {
  const editorRef = useRef();

  return (
    <div className={styles.editorContainer}>
      <EditorContextProvider
        decorators={decorators}
        initialRawContentState={initialRawContentState}
        pages={pages}
        onContentStateChange={onContentStateChange}
        type={type}
        editor={editorRef.current}
        mountEl={mountEl}
        onRemoveMention={onRemoveMention}
        additionalFilterQueryParams={additionalFilterQueryParams}
      >
        <EditorErrorBoundary>
          <Editor readOnly={readOnly} placeholder={placeholder} type={type} anchorRef={editorRef} spellCheck />
        </EditorErrorBoundary>
        <MentionSuggestions customPageType={type} />
        <PageEntityEditor />
        <MyOwnAreaSelect />
        <CreateNewPage />
      </EditorContextProvider>
    </div>
  );
}
